import styles from 'styles/modules/Card.module.scss';
import Link from 'next/link';
import Remove from 'components/Svg/Remove';
import SendOutline from 'components/Svg/SendOutline';
import ChevronRight from 'components/Svg/Card/ChevronRight';
import getCharacters from 'helpers/getCharacters';
import Button from 'components/Button';
import tagRemove from 'utils/tag-remove';
import safeHtml from 'utils/safe-html';
import Typo from 'components/Typo';
import formatDate from 'helpers/date';
import NewsAlertLogo from './Svg/NewsAlertLogo';

const isoDate = (value) => {
    // `new Date(null)` is the epoch, not an invalid date — guard falsy first
    const date = value ? new Date(value) : null;
    return !date || Number.isNaN(date.getTime())
        ? undefined
        : date.toISOString().slice(0, 10);
};

const CardOverlay = ({ link }) => {
    if (!link) return null;

    return (
        <Link href={link}>
            <a className={styles.overlayLink} />
        </Link>
    );
};

const CardRemove = ({ onRemove, id }) => {
    return (
        <button className={styles.close} onClick={() => onRemove(id)}>
            <Remove fill={'#C4C4C4'} fillCross={'#FFF'} />
        </button>
    );
};

const CardActionOpen = ({ link }) => {
    return (
        <a className={styles['action']} href={link}>
            <ChevronRight />
        </a>
    );
};

const CardApproval = ({ status, onApprove, id }) => {
    return (
        <Button
            theme={'primary'}
            classes={[styles.button]}
            onClick={() => onApprove(id)}
            title={status === 'pinned' ? 'UNPIN' : 'PIN'}
        />
    );
};

const CardTitle = ({ linkOverlay, title, link, titleLimit = 50 }) => {
    if (linkOverlay) {
        return (
            <Typo
                Tag={'h3'}
                variant={'h4'}
                className={['pb-xs', styles.title].join(' ')}
            >
                {getCharacters(title, titleLimit)}
            </Typo>
        );
    }

    // The styled block goes inside the link, as the internal branch already
    // did, so a real h3 renders with the classes the anchor used to carry.
    if (link) {
        // External link
        if (link.includes('http')) {
            return (
                <a href={link} target="_blank" rel="noreferrer">
                    <Typo
                        Tag={'h3'}
                        variant={'h4'}
                        className={['pb-xs', styles.title].join(' ')}
                    >
                        {getCharacters(title, titleLimit)}
                    </Typo>
                </a>
            );
        }

        // Internal link
        return (
            <Link href={link}>
                <a>
                    <Typo
                        Tag={'h3'}
                        variant={'h4'}
                        className={['pb-xs', styles.title].join(' ')}
                    >
                        {getCharacters(title, titleLimit)}
                    </Typo>
                </a>
            </Link>
        );
    }

    return null;
};

const CardBody = ({ body, limit = 300 }) => {
    return (
        <Typo
            variant={'body'}
            className={styles.body}
            dangerouslySetInnerHTML={{
                __html: safeHtml(getCharacters(tagRemove(body), limit)),
            }}
        />
    );
};

const CardCta = ({ status, cta, link, onApprove, id }) => {
    if (link.includes('http')) {
        return (
            <div className={styles.wrapperCta}>
                <a
                    className={['mt-xs', styles.cta].join(' ')}
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                >
                    {cta}
                </a>
                {onApprove && (
                    <CardApproval
                        status={status}
                        onApprove={onApprove}
                        id={id}
                    />
                )}
            </div>
        );
    }

    return (
        <div className={styles.wrapperCta}>
            <Link href={link}>
                <a className={['mt-xs', styles.cta].join(' ')}>{cta}</a>
            </Link>
            {onApprove && (
                <CardApproval status={status} onApprove={onApprove} id={id} />
            )}
        </div>
    );
};

const Card = ({
    id,
    pretitle,
    title,
    body,
    publishedAt,
    status,
    cta,
    link,
    linkOverlay,
    quotedOn,
    onRemove,
    onEdit,
    editLabel,
    onApprove = null,
    limit = 300,
    titleLimit = 50,
    alert = false,
    author = null,
    feed = false,
}) => {
    const isNonDefaultStatus = status && status !== 'publish';

    return (
        <div className={[styles.card].join(' ')}>
            <div className={['p-card', styles.cardWrapper].join(' ')}>
                {isNonDefaultStatus && (
                    <div
                        className={[
                            styles['card__status'],
                            status === 'new' ? styles['card__status--new'] : '',
                        ].join(' ')}
                    >
                        <span>{status.toUpperCase()}</span>
                    </div>
                )}
                {linkOverlay && <CardOverlay link={link} />}
                {onRemove && <CardRemove onRemove={onRemove} id={id} />}
                {link && <CardActionOpen link={link} />}
                {alert && (
                    <div className={['mb-xs', styles.logo].join(' ')}>
                        <NewsAlertLogo />
                    </div>
                )}
                {pretitle && (
                    <Typo variant={'caption'} className={'mb-s'}>
                        {pretitle}
                    </Typo>
                )}

                {quotedOn && (
                    <Typo variant={'body'} className={styles.body}>
                        Quoted on: {getCharacters(quotedOn, 120)}
                    </Typo>
                )}

                {title && (
                    <CardTitle
                        title={title}
                        linkOverlay={linkOverlay}
                        link={link}
                        titleLimit={titleLimit}
                    />
                )}
                {body && <CardBody body={body} limit={limit} />}
                {cta && link && (
                    <CardCta
                        cta={cta}
                        link={link}
                        onApprove={onApprove}
                        id={id}
                        status={status}
                    />
                )}
            </div>
            <div className={styles['card__actions']}>
                {onEdit && (
                    <button
                        type={'button'}
                        className={styles.edit}
                        aria-label={`${editLabel}: ${title}`}
                        onClick={() => onEdit(id)}
                    >
                        {editLabel}
                    </button>
                )}
                {publishedAt && (
                    <span className={styles['card__status']}>
                        <SendOutline />
                        <span>
                            Published:{' '}
                            <time dateTime={isoDate(publishedAt)}>
                                {formatDate(publishedAt)}
                            </time>
                            {feed && (
                                <>
                                    {', by '}
                                    {alert ? (
                                        <Link
                                            href={
                                                'https://newspage.co.uk/about-us/'
                                            }
                                        >
                                            <a className={styles.authorLink}>
                                                Dominic Hiatt
                                            </a>
                                        </Link>
                                    ) : (
                                        <Link href={`/${author.slug}`}>
                                            <a className={styles.authorLink}>
                                                {author.name}
                                            </a>
                                        </Link>
                                    )}
                                </>
                            )}
                        </span>
                    </span>
                )}
            </div>
        </div>
    );
};

export default Card;
